import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, Sparkles, XCircle } from "lucide-react";
import Navbar from "@/components/Navbar";

const FREE_FEATURES = [
    "LinkedIn, X, Instagram & Reddit posts",
    "Brand voice matching",
    "4-agent AI pipeline",
    "Quality scoring on every post",
    "Content calendar",
    "Source ingestion (URL & PDF)",
    "AI blog writer",
    "Up to 20 generations/month",
];

const SCHEMA = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Lakshmi",
    "description": "AI LinkedIn post writer and personal brand tool for Indian professionals",
    "url": "https://hilakshmi.com/pricing",
    "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "INR",
        "availability": "https://schema.org/InStock",
        "url": "https://hilakshmi.com/signup",
        "description": "Free plan — no credit card required"
    }
};

export default function Pricing() {
    useEffect(() => {
        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.text = JSON.stringify(SCHEMA);
        document.head.appendChild(s);
        document.title = "Pricing — Lakshmi AI is Free to Start | No Credit Card";
        return () => s.remove();
    }, []);

    return (
        <div className="min-h-screen">
            <Navbar />
            <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-24 text-center">
                <div className="lk-label mb-3">Simple pricing</div>
                <h1 className="font-heading text-4xl sm:text-6xl tracking-tight mb-4">
                    Free to start. <span className="lk-gradient-text">Always.</span>
                </h1>
                <p className="text-[var(--lk-muted)] text-base sm:text-lg max-w-xl mx-auto mb-14">
                    Lakshmi is free for Indian professionals who want to build their personal brand on LinkedIn and social media. No credit card. No hidden fees.
                </p>

                <div className="max-w-md mx-auto lk-border lk-surface rounded-3xl p-8 text-left relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-48 h-48 rounded-full blur-3xl pointer-events-none"
                        style={{ background: "rgba(124,58,237,0.10)" }} />
                    <div className="flex items-center gap-3 mb-2">
                        <Sparkles className="w-5 h-5 text-[var(--lk-primary)]" />
                        <span className="font-semibold text-lg">Free Plan</span>
                        <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                            style={{ background: "rgba(124,58,237,0.15)", color: "#9B5CF6", border: "1px solid rgba(124,58,237,0.3)" }}>
                            Beta
                        </span>
                    </div>
                    <div className="font-bold text-5xl mb-1">₹0</div>
                    <div className="text-sm text-[var(--lk-muted)] mb-8">No credit card required</div>

                    <ul className="space-y-3 mb-8">
                        {FREE_FEATURES.map(f => (
                            <li key={f} className="flex items-start gap-3 text-sm">
                                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                                <span>{f}</span>
                            </li>
                        ))}
                    </ul>

                    <Link to="/signup" className="lk-btn-primary rounded-xl px-6 py-3.5 flex items-center justify-center gap-2 text-sm w-full">
                        Get started free <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>

                <p className="mt-10 text-sm text-[var(--lk-muted)]">
                    Pro plan coming soon — <Link to="/signup" className="text-[var(--lk-primary)] hover:underline">join the waitlist</Link>
                </p>

                {/* Visual comparison: Lakshmi vs writing yourself */}
                <div className="mt-20 max-w-3xl mx-auto">
                    <h2 className="font-heading text-2xl sm:text-3xl tracking-tight mb-8 text-center">
                        Why <span className="lk-gradient-text">Lakshmi</span> vs writing yourself?
                    </h2>
                    <div className="grid sm:grid-cols-2 gap-5">
                        <div className="lk-border lk-surface rounded-2xl p-6">
                            <div className="text-sm font-semibold text-[var(--lk-muted)] mb-4 flex items-center gap-2">
                                <XCircle className="w-4 h-4 text-red-400" />
                                Writing manually
                            </div>
                            <ul className="space-y-3 text-sm text-[var(--lk-muted)]">
                                {["30–90 min per post", "Staring at blank screen", "Inconsistent posting cadence", "No quality benchmark", "One platform at a time", "Voice drifts over time"].map(t => (
                                    <li key={t} className="flex items-start gap-2"><span className="text-red-400/60 mt-0.5">✕</span>{t}</li>
                                ))}
                            </ul>
                        </div>
                        <div className="lk-border rounded-2xl p-6" style={{ borderColor: "rgba(124,58,237,0.4)", background: "rgba(124,58,237,0.04)" }}>
                            <div className="text-sm font-semibold text-[var(--lk-primary)] mb-4 flex items-center gap-2">
                                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                                With Lakshmi (free)
                            </div>
                            <ul className="space-y-3 text-sm">
                                {["Under 60 seconds", "Just drop a rough idea", "Post every day effortlessly", "Quality scored 0–100", "4 platforms simultaneously", "Your voice, every time"].map(t => (
                                    <li key={t} className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />{t}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                    {/* Lakshmi image accent */}
                    <div className="mt-12 flex justify-center">
                        <picture>
                            <source srcSet="/image/lakshmi_nobg.webp" type="image/webp" />
                            <img src="/image/lakshmi_nobg_opt.png" alt="Lakshmi AI"
                                className="w-32 sm:w-40 lk-float" loading="lazy"
                                style={{ filter: "drop-shadow(0 0 24px rgba(124,58,237,0.35))" }} />
                        </picture>
                    </div>
                </div>
            </div>
        </div>
    );
}
